import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  firmarUrl,
  refAFirmar,
  type ClienteStorageFirma,
  type OpcionesFirma,
  type RefStorage,
} from "@/lib/urlFirmada";

/**
 * Envoltorio de `@/lib/urlFirmada` con el cliente real de Supabase. Es la puerta única para
 * LEER un documento de Storage desde el admin: href, window.open, <img>, <iframe> o fetch del
 * archivo. Las URLs públicas guardadas en tablas pasan por aquí antes de usarse.
 */

/** Host del proyecto Supabase de esta sesión: una URL de otro proyecto no se firma aquí. */
export const HOST_PROYECTO = (() => {
  try {
    return new URL(import.meta.env.VITE_SUPABASE_URL as string).host;
  } catch {
    return "";
  }
})();

const cliente = supabase.storage as unknown as ClienteStorageFirma;

type Entrada = string | RefStorage | null | undefined;
type Opciones = Omit<OpcionesFirma, "hostProyecto" | "ahora">;

/** URL firmada (o la misma si no es de Storage). `null` si no hay nada que abrir. */
export async function obtenerUrlFirmada(entrada: Entrada, opciones: Opciones = {}): Promise<string | null> {
  // El fragmento (`#page=2`) no es parte de la ruta del objeto: se firma sin él y se repone.
  const i = typeof entrada === "string" ? entrada.indexOf("#") : -1;
  if (typeof entrada === "string" && i >= 0) {
    const firmada = await firmarUrl(cliente, entrada.slice(0, i), { ...opciones, hostProyecto: HOST_PROYECTO });
    return firmada ? `${firmada}${entrada.slice(i)}` : null;
  }
  return firmarUrl(cliente, entrada, { ...opciones, hostProyecto: HOST_PROYECTO });
}

/**
 * Abre el documento en otra pestaña con URL firmada. La pestaña se abre en el mismo clic (antes
 * de esperar la firma) para que el navegador no la bloquee como popup.
 */
export async function abrirUrlFirmada(entrada: Entrada, opciones: Opciones = {}): Promise<boolean> {
  if (!entrada) return false;
  const necesitaFirma = !!refAFirmar(entrada, HOST_PROYECTO);
  if (!necesitaFirma && typeof entrada === "string") {
    window.open(entrada, "_blank", "noopener,noreferrer");
    return true;
  }
  const pestana = window.open("", "_blank");
  const url = await obtenerUrlFirmada(entrada, opciones);
  if (!url) {
    pestana?.close();
    return false;
  }
  if (pestana) {
    pestana.opener = null;
    pestana.location.href = url;
  } else {
    window.open(url, "_blank", "noopener,noreferrer");
  }
  return true;
}

/** `fetch` del archivo con URL firmada (descargas a blob, ZIP, conteo de páginas). */
export async function fetchFirmado(entrada: Entrada, init?: RequestInit): Promise<Response> {
  const url = await obtenerUrlFirmada(entrada);
  if (!url) throw new Error("Documento sin URL");
  return fetch(url, init);
}

/**
 * URL firmada para `<img src>` / `<iframe src>`. Mientras firma devuelve `null` (no la pública,
 * para no pedir dos veces el archivo).
 */
export function useUrlFirmada(entrada: Entrada, opciones: Opciones = {}) {
  const clave = typeof entrada === "string" ? entrada : entrada ? `${entrada.bucket}/${entrada.path}` : "";
  const { expiraEn, descarga } = opciones;
  const [estado, setEstado] = useState<{ clave: string; url: string | null }>({ clave: "", url: null });

  useEffect(() => {
    if (!clave) {
      setEstado({ clave: "", url: null });
      return;
    }
    let vivo = true;
    obtenerUrlFirmada(entrada, { expiraEn, descarga }).then((url) => {
      if (vivo) setEstado({ clave, url });
    });
    return () => {
      vivo = false;
    };
    // `entrada` se representa por `clave`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clave, expiraEn, descarga]);

  const listo = estado.clave === clave;
  return { url: listo ? estado.url : null, cargando: !!clave && !listo };
}

/**
 * `onClick` para un `<a href={urlGuardada}>` existente: abre el documento con URL firmada en
 * lugar de la pública (con `{ descarga: true }` la firma fuerza descarga, como antes el atributo
 * `download` / `?download=`). Si la URL no es de Storage deja que el enlace siga normal.
 */
export function clicDocumentoFirmado(entrada: Entrada, opciones: Opciones = {}) {
  return (e: { preventDefault: () => void }) => {
    if (!entrada || !refAFirmar(entrada, HOST_PROYECTO)) return;
    e.preventDefault();
    void abrirUrlFirmada(entrada, opciones);
  };
}

/**
 * Baja el archivo sin abrir otra pestaña: Storage lo manda como adjunto con la URL firmada
 * (DESIGN §1, archivos dentro de la plataforma). Fuera de Storage no hay forma de forzar el
 * adjunto y esa sí va a otra pestaña.
 */
export async function descargarDocumento(entrada: Entrada, nombre?: string): Promise<boolean> {
  if (!entrada) return false;
  const deStorage = !!refAFirmar(entrada, HOST_PROYECTO);
  const url = await obtenerUrlFirmada(entrada, deStorage ? { descarga: nombre ?? true } : {});
  if (!url) return false;
  const a = document.createElement("a");
  a.href = url;
  if (!deStorage) {
    a.target = "_blank";
    a.rel = "noopener noreferrer";
  }
  a.click();
  return true;
}

/** `onClick` para un `<a>` de "Descargar" (ver `descargarDocumento`). */
export function clicDescargarFirmado(entrada: Entrada, nombre?: string) {
  return (e: { preventDefault: () => void }) => {
    if (!entrada) return;
    e.preventDefault();
    void descargarDocumento(entrada, nombre);
  };
}
